import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  MedicalServices,
  AttachMoney,
  AccessTime,
  LocationOn,
  CheckCircle,
  Warning,
  Info,
  PlayArrow,
  Stop,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listMedicalEquipment,
  createMedicalEquipment,
  updateMedicalEquipment,
  deleteMedicalEquipment,
  getAvailableEquipment,
  assignEquipmentToPatient,
  listPatientEquipmentAssignments,
  endEquipmentAssignment,
  calculateAssignmentCharges,
} from "../../../../api/medicalEquipment";

const MedicalEquipmentManager = ({ admissionId, patientName, admissionType = 'ipd' }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();

  // State
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [endAssignmentDialogOpen, setEndAssignmentDialogOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [assignmentToEnd, setAssignmentToEnd] = useState(null);
  const [endTime, setEndTime] = useState("");
  const [equipmentFormData, setEquipmentFormData] = useState({
    name: "",
    description: "",
    equipment_type: 6,
    charge_type: 1,
    hourly_rate: 0,
    daily_rate: 0,
    per_use_rate: 0,
    fixed_rate: 0,
    location: "",
    serial_number: "",
    notes: "",
  });

  // Fetch data
  const { data: equipment, isLoading: equipmentLoading } = useQuery({
    queryKey: ["medical-equipment"],
    queryFn: () => listMedicalEquipment({}, token),
  });

  const { data: availableEquipment } = useQuery({
    queryKey: ["available-equipment"],
    queryFn: () => getAvailableEquipment(token),
  });

  const { data: assignments, isLoading: assignmentsLoading } = useQuery({
    queryKey: ["equipment-assignments", admissionId, admissionType],
    queryFn: () => {
      const params = admissionType === 'icu' 
        ? { icu_admission: admissionId }
        : { admission: admissionId };
      return listPatientEquipmentAssignments(params, token);
    },
    enabled: !!admissionId,
  });

  // Mutations
  const createEquipmentMutation = useMutation({
    mutationFn: (data) => createMedicalEquipment(data, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["medical-equipment"]);
      queryClient.invalidateQueries(["available-equipment"]);
      setEquipmentDialogOpen(false);
      resetForm();
    },
  });

  const updateEquipmentMutation = useMutation({
    mutationFn: ({ id, data }) => updateMedicalEquipment(id, data, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["medical-equipment"]);
      queryClient.invalidateQueries(["available-equipment"]);
      setEquipmentDialogOpen(false);
      resetForm();
    },
  });

  const deleteEquipmentMutation = useMutation({
    mutationFn: (id) => deleteMedicalEquipment(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["medical-equipment"]);
      queryClient.invalidateQueries(["available-equipment"]);
    },
  });

  const assignEquipmentMutation = useMutation({
    mutationFn: ({ equipmentId, data }) =>
      assignEquipmentToPatient(equipmentId, data, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["equipment-assignments"]);
      queryClient.invalidateQueries(["available-equipment"]);
      setAssignmentDialogOpen(false);
    },
  });

  const endAssignmentMutation = useMutation({
    mutationFn: ({ id, endTime }) => endEquipmentAssignment(id, endTime, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["equipment-assignments"]);
      queryClient.invalidateQueries(["available-equipment"]);
      setEndAssignmentDialogOpen(false);
      setAssignmentToEnd(null);
      setEndTime("");
    },
  });

  // Helper functions
  const resetForm = () => {
    setEquipmentFormData({
      name: "",
      description: "",
      equipment_type: 6,
      charge_type: 1,
      hourly_rate: 0,
      daily_rate: 0,
      per_use_rate: 0,
      fixed_rate: 0,
      location: "",
      serial_number: "",
      notes: "",
    });
    setSelectedEquipment(null);
  };

  const handleEditEquipment = (equipment) => {
    setSelectedEquipment(equipment);
    setEquipmentFormData({
      name: equipment.name,
      description: equipment.description || "",
      equipment_type: equipment.equipment_type,
      charge_type: equipment.charge_type,
      hourly_rate: equipment.hourly_rate,
      daily_rate: equipment.daily_rate,
      per_use_rate: equipment.per_use_rate,
      fixed_rate: equipment.fixed_rate,
      location: equipment.location || "",
      serial_number: equipment.serial_number || "",
      notes: equipment.notes || "",
    });
    setEquipmentDialogOpen(true);
  };

  const handleAddEquipment = () => {
    resetForm();
    setEquipmentDialogOpen(true);
  };

  const handleAssignEquipment = (equipment) => {
    setSelectedEquipment(equipment);
    setAssignmentDialogOpen(true);
  };

  const handleSubmitEquipment = () => {
    if (selectedEquipment) {
      updateEquipmentMutation.mutate({
        id: selectedEquipment.id,
        data: equipmentFormData,
      });
    } else {
      createEquipmentMutation.mutate(equipmentFormData);
    }
  };

  const handleSubmitAssignment = () => {
    const assignmentData = {
      start_time: new Date().toISOString(),
      notes: "",
    };
    
    // Set the appropriate admission field based on type
    if (admissionType === 'icu') {
      assignmentData.icu_admission = admissionId;
    } else {
      assignmentData.admission = admissionId;
    }

    assignEquipmentMutation.mutate({
      equipmentId: selectedEquipment.id,
      data: assignmentData,
    });
  };

  const handleEndAssignment = (assignment) => {
    setAssignmentToEnd(assignment);
    setEndTime(new Date().toISOString().slice(0, 16)); // Set current time as default
    setEndAssignmentDialogOpen(true);
  };

  const handleConfirmEndAssignment = () => {
    if (assignmentToEnd && endTime) {
      endAssignmentMutation.mutate({
        id: assignmentToEnd.id,
        endTime: endTime,
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return "success"; // Available
      case 2:
        return "warning"; // In Use
      case 3:
        return "info"; // Maintenance
      case 4:
        return "error"; // Out of Order
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return "Available";
      case 2:
        return "In Use";
      case 3:
        return "Maintenance";
      case 4:
        return "Out of Order";
      default:
        return "Unknown";
    }
  };

  const getEquipmentTypeText = (type) => {
    switch (type) {
      case 1:
        return "Respiratory";
      case 2:
        return "Cardiovascular";
      case 3:
        return "Monitoring";
      case 4:
        return "Therapeutic";
      case 5:
        return "Diagnostic";
      case 6:
        return "Other";
      default:
        return "Unknown";
    }
  };

  const getChargeTypeText = (type) => {
    switch (type) {
      case 1:
        return "Hourly";
      case 2:
        return "Daily";
      case 3:
        return "Per Use";
      case 4:
        return "Fixed";
      default:
        return "Unknown";
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6">Medical Equipment & Processes</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddEquipment}
        >
          Add Equipment
        </Button>
      </Box>

      {/* Current Assignments */}
      {admissionId && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Equipment Assignments for {patientName}
          </Typography>
          {assignmentsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : assignments && assignments.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Equipment</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Start Time</TableCell>
                    <TableCell>End Time</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Total Charges</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.map((assignment) => (
                    <TableRow key={assignment.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <MedicalServices color="primary" />
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {assignment.equipment_name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {assignment.equipment_description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={assignment.equipment_type}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(assignment.start_time).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {assignment.end_time
                          ? new Date(assignment.end_time).toLocaleString()
                          : "Ongoing"}
                      </TableCell>
                      <TableCell>
                        {assignment.status === 1 ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <AccessTime color="warning" />
                            <Typography variant="body2">
                              {Math.round(
                                ((new Date() -
                                  new Date(assignment.start_time)) /
                                  (1000 * 60 * 60)) *
                                  100
                              ) / 100}{" "}
                              hours
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            {assignment.duration_hours || 0} hours
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="primary"
                        >
                          {assignment.total_charges ||
                            assignment.current_charges}{" "}
                          AFN
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {assignment.status === 1 ? (
                          <Tooltip title="End Assignment">
                            <IconButton
                              color="error"
                              onClick={() => handleEndAssignment(assignment)}
                              disabled={endAssignmentMutation.isPending}
                            >
                              <Stop />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Chip
                            label="Completed"
                            color="success"
                            size="small"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No equipment currently assigned to this patient.
            </Alert>
          )}
        </Paper>
      )}

      {/* Available Equipment */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Available Equipment
        </Typography>
        {equipmentLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {availableEquipment?.map((equipment) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={equipment.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <MedicalServices color="primary" />
                      <Typography
                        variant="h6"
                        sx={{ flexGrow: 1, fontSize: "1rem" }}
                      >
                        {equipment.name}
                      </Typography>
                      <Chip
                        label={getStatusText(equipment.status)}
                        size="small"
                        color={getStatusColor(equipment.status)}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      mb={2}
                      sx={{
                        flexGrow: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {equipment.description}
                    </Typography>
                    <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                      <Chip
                        label={getEquipmentTypeText(equipment.equipment_type)}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                      <Chip
                        label={getChargeTypeText(equipment.charge_type)}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color="primary"
                      mb={2}
                    >
                      Rate: {equipment.current_rate} AFN (
                      {getChargeTypeText(equipment.charge_type).toLowerCase()})
                    </Typography>
                    {equipment.location && (
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <LocationOn fontSize="small" color="action" />
                        <Typography variant="caption" sx={{ flexGrow: 1 }}>
                          {equipment.location}
                        </Typography>
                      </Box>
                    )}
                    <Box display="flex" gap={1} mt="auto">
                      {admissionId && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<PlayArrow />}
                          onClick={() => handleAssignEquipment(equipment)}
                          disabled={assignEquipmentMutation.isPending}
                          sx={{ flexGrow: 1 }}
                        >
                          Assign
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() => handleEditEquipment(equipment)}
                        sx={{ flexGrow: 1 }}
                      >
                        Edit
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Add/Edit Equipment Dialog */}
      <Dialog
        open={equipmentDialogOpen}
        onClose={() => setEquipmentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedEquipment ? "Edit Equipment" : "Add New Equipment"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Equipment Name"
                value={equipmentFormData.name}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    name: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={equipmentFormData.description}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    description: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Equipment Type</InputLabel>
                <Select
                  value={equipmentFormData.equipment_type}
                  onChange={(e) =>
                    setEquipmentFormData({
                      ...equipmentFormData,
                      equipment_type: e.target.value,
                    })
                  }
                >
                  <MenuItem value={1}>Respiratory</MenuItem>
                  <MenuItem value={2}>Cardiovascular</MenuItem>
                  <MenuItem value={3}>Monitoring</MenuItem>
                  <MenuItem value={4}>Therapeutic</MenuItem>
                  <MenuItem value={5}>Diagnostic</MenuItem>
                  <MenuItem value={6}>Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Charge Type</InputLabel>
                <Select
                  value={equipmentFormData.charge_type}
                  onChange={(e) =>
                    setEquipmentFormData({
                      ...equipmentFormData,
                      charge_type: e.target.value,
                    })
                  }
                >
                  <MenuItem value={1}>Hourly</MenuItem>
                  <MenuItem value={2}>Daily</MenuItem>
                  <MenuItem value={3}>Per Use</MenuItem>
                  <MenuItem value={4}>Fixed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hourly Rate (AFN)"
                type="number"
                value={equipmentFormData.hourly_rate}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    hourly_rate: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Daily Rate (AFN)"
                type="number"
                value={equipmentFormData.daily_rate}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    daily_rate: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Per Use Rate (AFN)"
                type="number"
                value={equipmentFormData.per_use_rate}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    per_use_rate: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fixed Rate (AFN)"
                type="number"
                value={equipmentFormData.fixed_rate}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    fixed_rate: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={equipmentFormData.location}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    location: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Serial Number"
                value={equipmentFormData.serial_number}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    serial_number: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={equipmentFormData.notes}
                onChange={(e) =>
                  setEquipmentFormData({
                    ...equipmentFormData,
                    notes: e.target.value,
                  })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEquipmentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitEquipment}
            disabled={
              createEquipmentMutation.isPending ||
              updateEquipmentMutation.isPending
            }
          >
            {selectedEquipment ? "Update" : "Add"} Equipment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Equipment Dialog */}
      <Dialog
        open={assignmentDialogOpen}
        onClose={() => setAssignmentDialogOpen(false)}
      >
        <DialogTitle>Assign Equipment to Patient</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Assign <strong>{selectedEquipment?.name}</strong> to{" "}
            <strong>{patientName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Rate: {selectedEquipment?.current_rate} AFN (
            {getChargeTypeText(selectedEquipment?.charge_type).toLowerCase()})
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            The equipment will be assigned immediately and charges will start
            accumulating.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignmentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitAssignment}
            disabled={assignEquipmentMutation.isPending}
          >
            Assign Equipment
          </Button>
        </DialogActions>
      </Dialog>

      {/* End Assignment Dialog */}
      <Dialog
        open={endAssignmentDialogOpen}
        onClose={() => setEndAssignmentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>End Equipment Assignment</DialogTitle>
        <DialogContent>
          {assignmentToEnd && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Equipment:</strong> {assignmentToEnd.equipment_name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Start Time:</strong>{" "}
                {new Date(assignmentToEnd.start_time).toLocaleString()}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Current Duration:</strong>{" "}
                {Math.round(
                  ((new Date() - new Date(assignmentToEnd.start_time)) /
                    (1000 * 60 * 60)) *
                    100
                ) / 100}{" "}
                hours
              </Typography>
              <Divider sx={{ my: 2 }} />
              <TextField
                fullWidth
                label="End Time"
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ mt: 2 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Enter the time when the equipment usage ended. Charges will be
                calculated based on this time.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndAssignmentDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmEndAssignment}
            variant="contained"
            color="error"
            disabled={!endTime || endAssignmentMutation.isPending}
          >
            {endAssignmentMutation.isPending ? "Ending..." : "End Assignment"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicalEquipmentManager;
